import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Eye,
  LayoutDashboard,
  Loader2,
  Pencil,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import GlassBackground from "@/shared/components/glassBackground";
import { confirmDialog } from "@/shared/components/dialog";
import { useWorkspaces } from "@/features/workspaces/hooks/useWorkspaces";
import type { WorkspaceDetail } from "@/features/workspaces/models/Workspace";
import type { WidgetSpec } from "../models/Widget";
import { useDashboard } from "../hooks/useDashboard";
import { useWidgetData } from "../hooks/useWidgetData";
import { WidgetCard, widgetGridStyle } from "../components/WidgetCard";
import WidgetEditorModal from "../components/WidgetEditorModal";

const SECONDARY_BUTTON =
  "flex items-center gap-2 h-9 px-4 rounded-xl border border-black/5 bg-white/60 text-sm font-semibold text-foreground/70 hover:bg-white hover:text-accent hover:shadow-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed";

export default function WorkspaceDashboardPage() {
  const { workspaceId = "" } = useParams();
  const navigate = useNavigate();
  const { dashboard, loaded, loading, save, remove } = useDashboard(workspaceId);
  const { get } = useWorkspaces();
  const [workspace, setWorkspace] = useState<WorkspaceDetail | null>(null);

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [widgets, setWidgets] = useState<WidgetSpec[]>([]);
  const [editorWidget, setEditorWidget] = useState<WidgetSpec | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  // El workspace aporta su nombre y, sobre todo, las automatizaciones que un widget de
  // ejecuciones puede elegir como scope.
  useEffect(() => {
    let ignore = false;
    void (async () => {
      const detail = await get(workspaceId);
      if (!ignore && detail) setWorkspace(detail);
    })();
    return () => {
      ignore = true;
    };
  }, [get, workspaceId]);

  // El estado editable arranca del dashboard persistido; en modo lectura se re-sincroniza solo.
  useEffect(() => {
    if (!editing) {
      setName(dashboard?.name ?? "");
      setWidgets(dashboard?.widgets ?? []);
    }
  }, [dashboard, editing]);

  const persistedWidgets = dashboard?.widgets ?? [];
  const { dataById, refreshingIds, refresh } = useWidgetData(
    workspaceId,
    persistedWidgets,
  );

  // Sin dashboard todavía, el permiso de crearlo lo da el workspace, no el dashboard inexistente.
  const isOwner = dashboard?.isOwner ?? workspace?.isOwner ?? false;
  const shownWidgets = editing ? widgets : persistedWidgets;

  const moveWidget = (index: number, delta: number) => {
    setWidgets((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const applyWidget = (widget: WidgetSpec) => {
    setWidgets((prev) => {
      const index = prev.findIndex((w) => w.id === widget.id);
      if (index === -1) return [...prev, widget];
      const next = [...prev];
      next[index] = widget;
      return next;
    });
    setEditorOpen(false);
    setEditorWidget(null);
  };

  const handleSave = async () => {
    const saved = await save({ name: name || "Dashboard", widgets });
    if (saved) {
      setEditing(false);
      void refresh();
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirmDialog({
      title: "Eliminar dashboard",
      message: "Se elimina el dashboard y sus widgets. El workspace y sus automatizaciones no se tocan.",
      confirmLabel: "Eliminar",
      cancelLabel: "Cancelar",
      variant: "danger",
    });
    if (confirmed) {
      await remove();
      setEditing(false);
    }
  };

  return (
    <GlassBackground>
      <div className="flex h-full w-full flex-col">
        <header className="flex items-center gap-3 border-b border-black/5 bg-white/70 px-4 py-2.5 backdrop-blur-md">
          <button
            onClick={() => navigate(`/admin/workspaces/${workspaceId}`)}
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-black/5 bg-white/60 text-foreground/60 transition-all hover:bg-white hover:text-accent hover:shadow-sm"
            title="Volver al workspace"
          >
            <ArrowLeft className="size-4" />
          </button>

          {editing ? (
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre del dashboard"
              className="max-w-md flex-1 rounded-xl border border-transparent bg-transparent px-3 py-1.5 text-lg font-bold text-foreground transition-all placeholder:font-normal placeholder:text-foreground/30 hover:bg-black/[0.03] focus:border-accent/30 focus:bg-white/80 focus:outline-none"
            />
          ) : (
            <h1 className="max-w-md flex-1 truncate px-3 text-lg font-bold text-foreground">
              {dashboard?.name || workspace?.name || "Dashboard"}
            </h1>
          )}

          {!isOwner && (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1.5 text-[11px] font-bold text-amber-600">
              <Eye className="size-3.5" />
              SOLO LECTURA
            </span>
          )}

          <div className="ml-auto flex shrink-0 items-center gap-2">
            {isOwner && !editing && dashboard && (
              <>
                <button onClick={handleDelete} className={SECONDARY_BUTTON} title="Eliminar dashboard">
                  <Trash2 className="size-4" />
                </button>
                <button onClick={() => setEditing(true)} className={SECONDARY_BUTTON}>
                  <Pencil className="size-4" />
                  Editar
                </button>
              </>
            )}
            {isOwner && editing && (
              <>
                <button
                  onClick={() => {
                    setEditorWidget(null);
                    setEditorOpen(true);
                  }}
                  className={SECONDARY_BUTTON}
                >
                  <Plus className="size-4" />
                  Añadir widget
                </button>
                <button onClick={() => setEditing(false)} className={SECONDARY_BUTTON}>
                  Cancelar
                </button>
                <button
                  onClick={() => void handleSave()}
                  disabled={loading}
                  className="flex h-9 cursor-pointer items-center gap-2 rounded-xl bg-accent px-5 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                  Guardar
                </button>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-auto p-8">
          {!loaded ? (
            <div className="grid grid-cols-4 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-40 animate-pulse rounded-2xl bg-black/5" />
              ))}
            </div>
          ) : !dashboard && !editing ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-accent/10">
                <LayoutDashboard className="size-8 text-accent/50" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-foreground">
Este workspace aún no tiene dashboard
              </h3>
              <p className="mb-6 max-w-md text-sm text-foreground/50">
                Armá un tablero con KPIs, tablas y gráficos alimentados por tus integraciones y por
                la telemetría de ejecuciones de todas las automatizaciones del workspace.
              </p>
              {isOwner && (
                <button
                  onClick={() => setEditing(true)}
                  className="flex cursor-pointer items-center gap-2 rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:bg-accent/90"
                >
                  <Plus className="size-4" />
                  Crear dashboard
                </button>
              )}
            </div>
          ) : shownWidgets.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <p className="mb-6 text-sm text-foreground/50">
                Sin widgets todavía. Añadí el primero.
              </p>
              {isOwner && editing && (
                <button
                  onClick={() => {
                    setEditorWidget(null);
                    setEditorOpen(true);
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:bg-accent/90"
                >
                  <Plus className="size-4" />
                  Añadir widget
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-6" style={{ gridAutoRows: "min-content" }}>
              {shownWidgets.map((widget, index) => (
                <div key={widget.id} style={widgetGridStyle(widget)}>
                  <WidgetCard
                    widget={widget}
                    data={dataById[widget.id]}
                    refreshing={refreshingIds.has(widget.id)}
                    editing={editing}
                    onRefresh={() => void refresh([widget.id])}
                    onEdit={() => {
                      setEditorWidget(widget);
                      setEditorOpen(true);
                    }}
                    onDelete={() => setWidgets((prev) => prev.filter((w) => w.id !== widget.id))}
                    onMoveUp={() => moveWidget(index, -1)}
                    onMoveDown={() => moveWidget(index, 1)}
                  />
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {editorOpen && (
        <WidgetEditorModal
          workspaceId={workspaceId}
          workflows={workspace?.workflows ?? []}
          widget={editorWidget}
          onSave={applyWidget}
          onClose={() => {
            setEditorOpen(false);
            setEditorWidget(null);
          }}
        />
      )}
    </GlassBackground>
  );
}
